import { IUser } from "../type";
import { getAuthHeaders } from "../utils/authToken";
import { apiFetch, AuthSessionError } from "../utils/apiFetch";
import { getClientTraceHeaders } from "../utils/clientTraceHeaders";
import { logClientDiagnostic } from "../utils/clientDiagnosticLog";

function normalizeBirthdate(user: IUser): IUser {
  // В miniapi часть данных уже конвертируется в Date на фронте,
  // но в JWT-ветке birthdate чаще всего приходит как строка.
  if (user.birthdate && !(user.birthdate instanceof Date)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const d = new Date(user.birthdate as any);
    if (!Number.isNaN(d.getTime())) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (user as any).birthdate = d;
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (user as any).birthdate = undefined;
    }
  }
  return user;
}

export type TMeResponse =
  | { type: "success"; user: IUser }
  | { type: "no_session"; reason?: string };

export async function resetCookieSession(): Promise<void> {
  await apiFetch("/api/auth/reset-cookie-session", {
    method: "POST",
    credentials: "include",
  });
}

const GET_ME_TIMEOUT_MS = 25_000;

export async function getMe(): Promise<TMeResponse> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), GET_ME_TIMEOUT_MS);
  try {
    // IMPORTANT:
    // Access token is stored in HttpOnly cookie and may expire.
    // We rely on apiFetch() 401 interceptor to refresh access token using refresh cookie.
    const r = await apiFetch("/api/auth/me", {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    });
    if (!r.ok) {
      const authData = await r
        .json()
        .then((data) => ({
          code: typeof data?.code === "string" ? data.code : null,
          reason: typeof data?.reason === "string" ? data.reason : null,
          requestId: typeof data?.requestId === "string" ? data.requestId : null,
        }))
        .catch(() => ({ code: null, reason: null, requestId: null }));
      logClientDiagnostic({
        action: "auth.get_me_http",
        description: `GET /api/auth/me HTTP ${r.status}`,
        endpoint: "/api/auth/me",
        method: "GET",
        additionalData: {
          status: r.status,
          authCode: authData.code,
          authReason: authData.reason,
          requestId: authData.requestId,
        },
      });
      return { type: "no_session", reason: `http_${r.status}` };
    }
    const data = await r.json();
    return { type: "success", user: normalizeBirthdate(data.user as IUser) };
  } catch (e: unknown) {
    const err = e as {
      name?: string;
      message?: string;
      code?: string | null;
      reason?: string | null;
      requestId?: string | null;
    };
    if (err?.name === "AbortError") {
      logClientDiagnostic({
        action: "auth.get_me_timeout",
        description: `GET /api/auth/me aborted after ${GET_ME_TIMEOUT_MS}ms`,
        endpoint: "/api/auth/me",
        method: "GET",
        additionalData: { timeoutMs: GET_ME_TIMEOUT_MS },
      });
      return { type: "no_session", reason: "timeout" };
    }
    logClientDiagnostic({
      action: "auth.get_me_error",
      description: err?.message || "getMe failed",
      endpoint: "/api/auth/me",
      method: "GET",
      additionalData: {
        name: err?.name || null,
        authCode: err?.code || null,
        authReason: err?.reason || null,
        requestId: err?.requestId || null,
        isAuthSessionError: err instanceof AuthSessionError,
      },
    });
    return { type: "no_session", reason: "network_or_auth" };
  } finally {
    window.clearTimeout(timer);
  }
}

export type TTrackTelegramIdResponse =
  | { type: "success"; id_TG: number }
  | { type: "error"; message: string };

// Used only to remember Telegram chat id for currently authenticated user.
// Authentication itself is always done via JWT cookies.
export async function trackTelegramId(tg_id: number): Promise<TTrackTelegramIdResponse> {
  try {
    const r = await fetch("/api/auth/track-tg", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
        ...getClientTraceHeaders(),
      },
      credentials: "include",
      body: JSON.stringify({ tg_id }),
    });
    if (!r.ok) {
      const data = await r.json().catch(() => null);
      return { type: "error", message: data?.message || "Track tg_id error" };
    }
    const data = await r.json();
    return { type: "success", id_TG: data?.id_TG as number };
  } catch {
    return { type: "error", message: "Track tg_id network error" };
  }
}

/** Сообщить бэкенду, что ученик установил PWA / открывает из standalone (идемпотентно). */
export async function reportPwaInstalled(): Promise<void> {
  await apiFetch("/api/auth/pwa-installed", {
    method: "POST",
    credentials: "include",
  });
}

export type TPwaHeartbeatPayload = {
  platformHint: string;
  appVersion: string;
  pushPermissionState?: string;
};

/** Heartbeat из PWA standalone: платформа, версия, разрешение на push (см. ТЗ PWA §9). */
export async function sendPwaHeartbeat(payload: TPwaHeartbeatPayload): Promise<void> {
  await apiFetch("/api/auth/pwa-heartbeat", {
    method: "POST",
    headers: {
        "Content-Type": "application/json;charset=utf-8",
        ...getClientTraceHeaders(),
      },
    body: JSON.stringify(payload),
    credentials: "include",
  });
}


export type TAuthErrorResponse = { type: "error"; message: string };
export type TAuthSuccessResponse = { type: "success"; user: IUser };
export type TAuthResponse = TAuthSuccessResponse | TAuthErrorResponse;

function normalizeEmailForAuth(email: string): string {
  return String(email || "").trim().toLowerCase();
}

type TAuthApiError = {
  code?: string | null;
  reason?: string | null;
  message?: string | null;
};

function mapAuthErrorToHumanText(error: TAuthApiError, status: number): string {
  const code = String(error.code || "").toUpperCase();
  const reason = String(error.reason || "").toLowerCase();
  const rawMessage = String(error.message || "").trim();
  const rawMessageUpper = rawMessage.toUpperCase();

  if (status === 429 || code === "AUTH_RATE_LIMITED" || reason === "rate_limit_exceeded") {
    return "Слишком много попыток входа. Подождите немного и попробуйте снова.";
  }

  if (reason === "user_not_found" || reason === "invalid_password") {
    return "Неверный e-mail или пароль.";
  }

  if (reason === "password_hash_missing") {
    return "Для этого аккаунта вход по паролю пока недоступен. Обратитесь в поддержку.";
  }

  if (reason === "password_already_set" || rawMessageUpper === "PASSWORD_ALREADY_SET") {
    return "Аккаунт уже зарегистрирован. Войдите с вашим e-mail и паролем.";
  }

  if (reason === "refresh_cookie_missing" || reason === "access_cookie_missing") {
    return "Сессия не найдена. Пожалуйста, войдите снова.";
  }

  if (status >= 500) {
    return "Сервис временно недоступен. Попробуйте чуть позже.";
  }

  if (rawMessage) return rawMessage;
  return "Не удалось выполнить авторизацию. Попробуйте снова.";
}

export async function webLogin(input: {
  email: string;
  password: string;
}): Promise<TAuthResponse> {
  const payload = {
    ...input,
    email: normalizeEmailForAuth(input.email),
  };
  try {
    const r = await fetch("/api/auth/login", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
        ...getClientTraceHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const data = (await r.json().catch(() => null)) as TAuthApiError | null;
      return {
        type: "error",
        message: mapAuthErrorToHumanText(
          {
            code: data?.code,
            reason: data?.reason,
            message: data?.message,
          },
          r.status
        ),
      };
    }
    const data = await r.json();
    return { type: "success", user: normalizeBirthdate(data.user as IUser) };
  } catch {
    return { type: "error", message: "Не удалось выполнить вход" };
  }
}

export async function webRegister(input: {
  name: string;
  phone: string;
  email: string;
  password: string;
  confirmPassword: string;
}): Promise<TAuthResponse> {
  const payload = {
    ...input,
    email: normalizeEmailForAuth(input.email),
  };
  try {
    const r = await fetch("/api/auth/register", {
      method: "POST",
      headers: {
        "Content-Type": "application/json;charset=utf-8",
        ...getClientTraceHeaders(),
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    if (!r.ok) {
      const data = (await r.json().catch(() => null)) as TAuthApiError | null;
      return {
        type: "error",
        message: mapAuthErrorToHumanText(
          {
            code: data?.code,
            reason: data?.reason,
            message: data?.message,
          },
          r.status
        ),
      };
    }
    const data = await r.json();
    return { type: "success", user: normalizeBirthdate(data.user as IUser) };
  } catch {
    return { type: "error", message: "Не удалось зарегистрироваться" };
  }
}

export type TUpdateUserResponse = { type: "success"; user: IUser };

export async function updateUser(user: IUser): Promise<TUpdateUserResponse> {
  const prom = new Promise<TUpdateUserResponse>((res) => {
    apiFetch("/miniapi/update", {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({ ...user }),
      credentials: "include",
    })
      .then((r) => r.json())
      .then((r) => {
        if (r.user.birthdate) {
          r.user.birthdate = new Date(r.user.birthdate);
        }
        res({ type: "success", user: r.user as IUser });
      });
  });

  return await prom;
}

export async function update_avatar(
  submitBody: FormData
): Promise<TUpdateUserResponse> {
  const authHeaders = getAuthHeaders();
  // Для FormData нельзя вручную задавать application/json,
  // браузер сам ставит multipart/form-data с boundary.
  delete authHeaders["Content-Type"];

  const response = await apiFetch(`/miniapi/update_avatar`, {
    method: "POST",
    headers: authHeaders,
    body: submitBody,
    credentials: "include",
  });

  if (!response.ok) {
    throw new Error(`Avatar upload failed: ${response.status}`);
  }

  const r = await response.json();
  if (r.user?.birthdate) {
    r.user.birthdate = new Date(r.user.birthdate);
  }
  return { type: "success", user: r.user as IUser };
}

export type TGetSilver = {
  type: "success";
  summ: number;
};

export async function getSilver(): Promise<TGetSilver> {
  const res = new Promise<TGetSilver>((res) => {
    apiFetch("/miniapi/get_silver", {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify({
      }),
      credentials: "include",
    })
      .then((response) => response.json())
      .then((r) => {
        res({ type: "success", summ: r.summ as number });
      })
      .catch(() => {
        res({ type: "success", summ: 0 });
      });
  });
  return await res;
}
